// Disable HMR for middleware to prevent Nuxt virtual module conflicts
import { getCookie } from '@/utils/cookie'
import { completeRouteMiddleware } from '@/plugins/ar-axios'
import { isUnauthenticatedPath } from '@/utils/unauthenticatedPath'

if (import.meta.hot) {
  import.meta.hot.decline()
}

export default defineNuxtRouteMiddleware(async (to) => {
  // RK/NOTE/29-07-2019
  //
  // For now, any routes that do not require authentication need to
  // be hard-coded here because the default Layout automatically
  // attempts authentication and redirects to login page on failure.
  // TODO later: draft a simpler way to differentiate authenticated
  // pages with unauthenticated pages.
  //
  // The other issue is that for routes that don't need authentication
  // seem to be created/mounted twice. Not sure why this is the case,
  // it may be vuex recreating components based on a dispatch
  const _startTime = import.meta.client ? performance.now() : 0

  // Set on the cold-load path below. Captured as a flag because VERIFY_TOKEN
  // commits the account, so the branch condition no longer holds afterwards.
  let didVerifyToken = false

  try {
    const nuxtApp = useNuxtApp()
    if (nuxtApp.$store.getters['authModule/userPromoterOid'] === null && !getCookie('x-auth-token')) {
      if (
        !isUnauthenticatedPath(to.path)
        && to.matched?.[0].path !== '/message-preview/:hash()'
        && !to.fullPath.startsWith('/__storybook__')
      ) {
        console.log('No auth token found, redirecting to login page')
        // Use replace: true to avoid adding a navigation entry to the history
        // This helps prevent navigation conflicts
        return navigateTo('/authenticate/login', { replace: true })
      }
    }
    else if (nuxtApp.$store.getters['authModule/userPromoterOid'] === null && getCookie('x-auth-token')) {
      // If there is no promoter account then we need to populate it, otherwise don't.
      // Check for promoter account? if we don't have one then do the request.
      await nuxtApp.$store.dispatch('authModule/VERIFY_TOKEN')
      didVerifyToken = true

      // After session is verified, redirect to AM2 if the user lacks the beta-tester flag.
      // This handles the first-load race condition where route-redirect runs before the cookie is set.
      const config = useRuntimeConfig()
      if (import.meta.client && config.public.ENABLED_HYBRID_SETUP) {
        const hasBetaTester = nuxtApp.$store.getters['authModule/isFeatureEnabled'](['beta-tester'])
        if (hasBetaTester === false && window.location.hostname === config.public.AM3_SUBDOMAIN) {
          window.location.href = `${window.location.protocol}//${config.public.AM2_SUBDOMAIN}${to.fullPath}`
          return
        }
      }
    }
    else {
      // Do nothing if promoter oid exists
    }

    // Region settings carry the promoter's currency, which every money display
    // falls back to when a value has no currency of its own. Without them
    // `regionSettings` stays null and money renders as USD for every promoter.
    //
    // The gating decision — dispatch at all? await the cold-load paint budget?
    // — and the concurrent-call dedupe live in ENSURE_REGION_SETTINGS, so they
    // can be unit-tested (vitest excludes client/middleware/**). `isColdLoad`
    // is the only signal the middleware holds that the action cannot derive:
    // VERIFY_TOKEN commits the account, so afterwards the state looks identical
    // to an in-SPA navigation.
    await nuxtApp.$store.dispatch('authModule/ENSURE_REGION_SETTINGS', {
      isColdLoad: didVerifyToken,
    })
  }
  catch (error) {
    console.log(error)
  }
  finally {
    // Signal that the authentication middleware has completed
    // This ensures any API requests waiting can proceed
    if (import.meta.client) {
      completeRouteMiddleware()
    }
  }
})
